import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Dark mode for the staff side (owner, Sep 21 2026): "follow the phone, plus
 * a switch". Auto matches the phone or computer; Light and Dark pin it. The
 * choice is kept on this device only.
 *
 * Customer pages never go dark. A quote, an invoice, the customer's own page,
 * a referral link and the plans page always look the way the customer
 * expects, whatever their phone is set to. Sales mode (/sales) was built
 * dark from the start and keeps its own look either way. public/index.html
 * repeats this list in its first-paint script, so keep the two in step.
 */
export type ThemePref = 'auto' | 'light' | 'dark';

const KEY = 'kam-theme';
const OWN_LOOK = /^\/(q|invoice|my|r)\/|^\/(plans|sales)(\/|$)/;

export const keepsOwnLook = (path: string) => OWN_LOOK.test(path);

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch { return 'auto'; }
}

function systemDark() {
  try { return window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
}

function paint(dark: boolean) {
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  // The phone's own status bar follows along.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', dark ? '#1c1820' : '#df00ff');
}

type ThemeCtx = { pref: ThemePref; setPref: (p: ThemePref) => void; dark: boolean };
const Ctx = createContext<ThemeCtx>({ pref: 'auto', setPref: () => {}, dark: false });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [sysDark, setSysDark] = useState<boolean>(systemDark);

  // Follow the phone live: flipping the phone to dark at sunset flips the app.
  useEffect(() => {
    let mq: MediaQueryList;
    try { mq = window.matchMedia('(prefers-color-scheme: dark)'); } catch { return; }
    const on = (e: MediaQueryListEvent) => setSysDark(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);

  const wantsDark = pref === 'dark' || (pref === 'auto' && sysDark);
  const dark = wantsDark && !keepsOwnLook(pathname);
  useEffect(() => { paint(dark); }, [dark]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try { if (p === 'auto') localStorage.removeItem(KEY); else localStorage.setItem(KEY, p); } catch {}
  }, []);

  return <Ctx.Provider value={{ pref, setPref, dark }}>{children}</Ctx.Provider>;
}

export const useTheme = () => useContext(Ctx);
